import type { SearchTreeNode } from '@/types/searchTreeTypes';
import { atom } from 'jotai';

/**
 * Context for advanced search when the initial tree came from a template or URL (?tree=...).
 * Not used when loading a saved search by ID (tree and metadata come from API/searchData).
 */
export type SearchTemplateData = {
  templateName?: string;
  templateId?: number;
  /** Tree used to seed the builder. */
  initialTree: SearchTreeNode;
  source: 'direct' | 'url';
};

export const searchTemplateDataAtom = atom<SearchTemplateData | undefined>(undefined);

/**
 * Incremented when Apply is clicked from the Restart modal. Used as a key to force
 * AdvancedSearchListCreatorPage to remount with the new tree.
 */
export const restartKeyAtom = atom(0);

/**
 * Shape of the parsed `tree` URL param. May be a full payload (from template preview)
 * or a raw tree. Used so URL parsing is typed instead of untyped.
 */
export type TreeUrlPayload =
  | { templateName?: string; templateId?: number; searchTreeNode: SearchTreeNode }
  | SearchTreeNode;

/**
 * Build SearchTemplateData from a parsed URL payload.
 * Handles both object-with-searchTreeNode and raw SearchTreeNode shapes.
 */
export function searchTemplateDataFromTreeUrlPayload(
  payload: TreeUrlPayload | null
): SearchTemplateData | undefined {
  if (payload == null) return undefined;
  if ('searchTreeNode' in payload && payload.searchTreeNode != null) {
    return {
      templateName: payload.templateName,
      templateId: payload.templateId,
      initialTree: payload.searchTreeNode,
      source: 'url',
    };
  }
  if (typeof payload === 'object' && payload !== null && 'op' in payload) {
    return {
      initialTree: payload as SearchTreeNode,
      source: 'url',
    };
  }
  return undefined;
}
